import { useEffect } from "react";

import { NetworkStatus, useQuery } from "@apollo/client";

import {
  GetNodesByCustomerQuery,
  GetNodesByCustomerQueryVariables,
} from "../../../../API";
import { useCustomerIdGuard } from "../../../hooks/useCustomerIdGuard";
import { GET_NODES_BY_CUSTOMER } from "../../../operations/queries";
import { errorNotification } from "../../../variables/notification";

export const useGetNodesByCustomer = () => {
  const selectedCustomerId = useCustomerIdGuard();

  const { data, fetchMore, loading, networkStatus } = useQuery<
    GetNodesByCustomerQuery,
    GetNodesByCustomerQueryVariables
  >(GET_NODES_BY_CUSTOMER, {
    variables: {
      limit: 300,
      customerId: selectedCustomerId,
    },
    skip: !selectedCustomerId,
    notifyOnNetworkStatusChange: true,
  });

  useEffect((): void => {
    const fetchAllPages = async () => {
      let currentNextToken = data?.getNodesByCustomer?.nextToken || "";

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              limit: 300,
              customerId: selectedCustomerId,
              nextToken: currentNextToken,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prev;

              // Merge nodes and their devices from previous and new results
              const mergedNodes = [...(prev.getNodesByCustomer?.items || [])];

              fetchMoreResult.getNodesByCustomer?.items?.forEach(newNode => {
                if (!newNode) return;

                // Find if this node already exists in previous results
                const existingNodeIndex = mergedNodes.findIndex(
                  node => node?.id === newNode.id
                );

                if (existingNodeIndex >= 0) {
                  // Create a Set of device IDs to avoid duplicates
                  const existingDeviceIds = new Set(
                    mergedNodes[existingNodeIndex]?.devices?.map(
                      device => device?.id
                    ) || []
                  );

                  // Filter out duplicate devices from the incoming node
                  const uniqueNewDevices = (newNode.devices || []).filter(
                    device => device && !existingDeviceIds.has(device.id)
                  );

                  // Update existing node with new data
                  mergedNodes[existingNodeIndex] = {
                    ...mergedNodes[existingNodeIndex],
                    ...newNode,
                    devices: [
                      ...(mergedNodes[existingNodeIndex]?.devices || []),
                      ...uniqueNewDevices,
                    ],
                  };
                } else {
                  // Add new node
                  mergedNodes.push(newNode);
                }
              });

              return {
                getNodesByCustomer: {
                  ...fetchMoreResult.getNodesByCustomer,
                  items: mergedNodes,
                },
              };
            },
          });

          currentNextToken = response.data?.getNodesByCustomer?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional device pages:", error);

        errorNotification();
      }
    };

    fetchAllPages();
  }, [selectedCustomerId, data?.getNodesByCustomer?.nextToken]);

  return {
    data,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
  };
};
